/**
 * chatPushRegistration.js
 *
 * Requests FCM web-push permission, gets the device's token, and stores
 * it under users/{uid}.fcmTokens.{token} so the chatMessageNotifier
 * Cloud Function can target this device for chat push notifications.
 *
 * Idempotent — repeated calls in the same session are no-ops. Safe to
 * call from any chat-open hook.
 */

import { getApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported as isMessagingSupported,
} from "firebase/messaging";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../firebase-config";

const VAPID_KEY = process.env.REACT_APP_FCM_VAPID_KEY || "";

let registrationPromise = null;
let foregroundUnsubscribe = null;

export async function registerChatPushNotifications({ onForeground } = {}) {
  if (registrationPromise) return registrationPromise;
  registrationPromise = (async () => {
    try {
      const supported = await isMessagingSupported();
      if (!supported) {
        console.log("[chat-push] FCM not supported in this browser");
        return { ok: false, reason: "unsupported" };
      }
      if (!auth?.currentUser?.uid) {
        return { ok: false, reason: "no-user" };
      }
      if (!VAPID_KEY) {
        console.warn("[chat-push] REACT_APP_FCM_VAPID_KEY not set");
        return { ok: false, reason: "no-vapid-key" };
      }

      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        console.log(`[chat-push] permission ${perm}`);
        return { ok: false, reason: `permission-${perm}` };
      }

      const messaging = getMessaging(getApp());
      let token;
      try {
        token = await getToken(messaging, { vapidKey: VAPID_KEY });
      } catch (e) {
        console.warn("[chat-push] getToken failed:", e.message);
        return { ok: false, reason: "get-token-failed", error: e.message };
      }
      if (!token) {
        return { ok: false, reason: "empty-token" };
      }

      const uid = auth.currentUser.uid;
      try {
        await setDoc(
          doc(db, "users", uid),
          {
            fcmTokens: {
              [token]: {
                addedAt: serverTimestamp(),
                userAgent: (navigator.userAgent || "").slice(0, 256),
              },
            },
          },
          { merge: true }
        );
      } catch (e) {
        console.warn("[chat-push] token persist failed:", e.message);
      }

      if (foregroundUnsubscribe) {
        try { foregroundUnsubscribe(); } catch (_) {}
      }
      foregroundUnsubscribe = onMessage(messaging, (payload) => {
        if (typeof onForeground === "function") {
          try { onForeground(payload); } catch (_) {}
        }
      });

      console.log(`[chat-push] registered token …${token.slice(-12)}`);
      return { ok: true, token };
    } catch (e) {
      console.warn("[chat-push] registration failed:", e.message);
      return { ok: false, reason: "exception", error: e.message };
    }
  })();
  return registrationPromise;
}
